import React, { useState } from 'react';
import Section from './Section';
import DiscoverySpecStates from './DiscoverySpecStates';
import ComponentListPane from '../../components/discovery/ComponentListPane';
import ComponentDetailPane from '../../components/discovery/ComponentDetailPane';
import SupplierDetailBlocks from '../../components/discovery/SupplierDetailBlocks';
import SupplierTrustMatrix from '../../components/discovery/SupplierTrustMatrix';
import TrustChip from '../../components/discovery/TrustChip';
import { ComponentListItem, LineKeySpec, SupplierView } from '../../components/discovery/discoveryModel';
import {
  ESC_LINE,
  ESC_SUPPLIERS,
  GROUND_STATION,
  GROUND_STATION_LINE,
  LIPO_30C,
  LIPO_40C,
  LIPO_LINE,
  LIPO_LINE_40C,
} from './discoveryAlternativeFixtures';

/**
 * /design gallery for the Supplier Discovery v2.1 layout — sample data to
 * eyeball every state against `Supplier Discovery v2.dc.html` (real research
 * needs auth, so states are exercised here): pick cards with the
 * three-metric row, expanded detail blocks (dashed track record), the
 * other-suppliers accordion, and the Supplier Trust Matrix. The LiPo line
 * (stage RFQ-134) shows the "Specs found" chips: its own 30C with one
 * supplier, 40C and 35C as alternatives, Robbie's one suggestion, a read-only
 * preview, and the switch (and back). The ground station (RFQ-135) is one
 * supplier card with two products.
 */

const LIST_ITEMS: ComponentListItem[] = [
  { key: 'c1', name: 'Hub motor, brushless DC, 48V', partNumber: 'HM-4820-BLDC', mpn: 'G020.250.DC', phase: 'ready', supplierCount: 6, pickedCount: 3 },
  { key: 'c2', name: 'Optical encoder, 2048 PPR', partNumber: 'ENC-2048-OPT', phase: 'ready', supplierCount: 2, pickedCount: 0 },
  { key: 'c3', name: 'Deep-groove ball bearing', partNumber: 'BRG-6204-2RS', phase: 'searching', supplierCount: 0, pickedCount: 0 },
  { key: 'c4', name: 'Power cable, AWG12', partNumber: 'CBL-PWR-AWG12', phase: 'failed', supplierCount: 0, pickedCount: 0 },
  { key: 'c5', name: '9-DOF IMU module', mpn: 'BNO085', phase: 'empty', supplierCount: 0, pickedCount: 0 },
  { key: 'c6', name: 'LiPo battery', phase: 'ready', supplierCount: 6, pickedCount: 0, matchingSupplierCount: 1, otherSpecCount: 2 },
  { key: 'c7', name: GROUND_STATION_LINE.name, phase: 'ready', supplierCount: 3, pickedCount: 1 },
  { key: 'c8', name: ESC_LINE.name, phase: 'ready', supplierCount: 3, pickedCount: 0 },
];


/** Rich candidates — every negotiation-intelligence field present. */
const RICH_SUPPLIERS: SupplierView[] = [
  {
    name: 'Oriental Drives EU',
    location: 'Düsseldorf, DE',
    trustScore: 56,
    belowTrustBar: true,
    trust: {
      band: 'Silver',
      confidence: 'low',
      status: 'scored',
      summary: 'Adequate evidence of reliability. Based on limited public evidence.',
      supports: [
        { text: '4.6★ from 420 Google reviews', sourceUrl: 'https://example.com' },
        { text: 'In business since 1998 (28 years)' },
        { text: 'Listed by Oriental Motor as an authorised partner', sourceUrl: 'https://example.com' },
      ],
      concerns: [
        { text: 'Not independently verified yet: financials' },
        { text: 'No completed orders on Robbie yet' },
      ],
    },
    rank: 1,
    reason: 'EU stock for 48V BLDC hub motors — shortest delivery to Rotterdam of all candidates. Ranked #1 on your criteria: quality first, 4-week lead inside your window.',
    advantages: [],
    risks: [],
    quotedPrice: 224,
    currencySymbol: '€',
    leadTimeWeeks: 4,
    websiteUrl: 'https://example.com',
    hasContactEmail: true,
    priceTiers: [{ qty: 10, unitPrice: 205, currencySymbol: '€' }],
    negotiableTo: { price: 205, basis: 'their qty-10 price' },
    freightTerms: 'quoted separately at €40/order',
    warranty: '2 years',
    stockStatus: 'EU stock, ships weekly',
    specMatch: [
      { requirement: '48 V nominal', offered: '48 V', met: 'met' },
      { requirement: '200 W continuous', offered: '220 W continuous', met: 'met' },
      { requirement: 'Encoder-ready, 2048 PPR', offered: 'Built-in 2048 PPR', met: 'met' },
      { requirement: '12 mm keyed shaft', offered: '12 mm keyed', met: 'met' },
      { requirement: 'IP54 minimum', offered: 'IP65 — exceeds', met: 'exceeds' },
      { requirement: '16 h/day duty cycle', offered: 'Rated continuous', met: 'met' },
    ],
    compliance: [
      { name: 'ISO 9001', held: true },
      { name: 'RoHS', held: true },
      { name: 'REACH', held: true },
      { name: 'CE marking', held: true },
      { name: 'UL listed', held: 'unknown' },
    ],
  },
  {
    name: 'Nova Motion Works',
    location: 'Eindhoven, NL',
    trustScore: 89,
    rank: 2,
    reason: 'Local to your delivery point — new Tier 1 that matches your warehouse bot architecture.',
    advantages: [],
    risks: [],
    quotedPrice: 218,
    currencySymbol: '€',
    leadTimeWeeks: 5,
    websiteUrl: 'https://example.com',
    hasContactEmail: false,
    negotiableTo: { price: 199, basis: 'their qty-10 price' },
    specMatch: [
      { requirement: '48 V nominal', offered: '48 V', met: 'met' },
      { requirement: '200 W continuous', offered: '200 W continuous', met: 'met' },
      { requirement: 'IP54 minimum', offered: undefined, met: 'unknown' },
      { requirement: '12 mm keyed shaft', offered: '10 mm plain', met: 'miss' },
    ],
    compliance: [
      { name: 'ISO 9001', held: true },
      { name: 'RoHS', held: 'unknown' },
      { name: 'UL listed', held: false },
    ],
  },
  {
    name: 'Maxon Drive Systems',
    location: 'Sachseln, CH',
    trustScore: 96,
    rank: 3,
    reason: 'Best trust score — lead time misses week 42',
    advantages: [],
    risks: [],
    quotedPrice: 212,
    currencySymbol: '€',
    leadTimeWeeks: 9,
    hasContactEmail: true,
    warranty: '3 years',
    specMismatches: [{ key: 'voltage', label: 'Voltage', requested: '48V', offered: '24V', kind: 'different' }],
  },
  {
    name: 'Shenzhen Kinetix',
    outsideSourcingLocation: true,
    trustScore: 78,
    rank: 4,
    reason: 'Lowest price — kept as your cost benchmark',
    advantages: [],
    risks: [],
    quotedPrice: 176,
    currencySymbol: '€',
    leadTimeWeeks: 10,
    hasContactEmail: true,
  },
  {
    name: 'Bosch Rexroth Motion',
    trustScore: 94,
    rank: 5,
    reason: 'No stock until Q4 · MOQ 50 units',
    advantages: [],
    risks: [],
    quotedPrice: 245,
    currencySymbol: '€',
    leadTimeWeeks: 6,
    moq: 50,
    hasContactEmail: true,
  },
  // No price, no score, no research fields — every honest absent-state at once.
  { name: 'AnyTech Servo Co.', advantages: [], risks: [], currencySymbol: '', hasContactEmail: false },
];

/** Old-shape research: no numbers, no ranking — dashes everywhere, no crash. */
const LEGACY_SUPPLIERS: SupplierView[] = [
  { name: 'Encoder Works GmbH', location: 'Jena, DE', reason: 'Stated distributor for optical encoders', advantages: [], risks: [], currencySymbol: '', hasContactEmail: true },
  { name: 'OptoSense Ltd.', advantages: [], risks: [], currencySymbol: '', hasContactEmail: false, findingContact: true },
];

const DiscoveryGallery: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState<string>('c1');
  const [filter, setFilter] = useState('');
  // Fixtures without listing ids tick by supplier name — the pre-listing payload shape.
  const [ticked, setTicked] = useState<string[]>(['Oriental Drives EU', 'Maxon Drive Systems', 'Shenzhen Kinetix', 'mt-h7']);
  const [lipoSwapped, setLipoSwapped] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const selected = LIST_ITEMS.find((it) => it.key === selectedKey) ?? LIST_ITEMS[0];
  const SUPPLIERS_BY_KEY: Record<string, SupplierView[]> = {
    c2: LEGACY_SUPPLIERS,
    c6: lipoSwapped ? LIPO_40C : LIPO_30C,
    c7: GROUND_STATION,
    c8: ESC_SUPPLIERS,
  };
  const LINE_BY_KEY: Record<string, { quantity: number; description: string; keySpecs: LineKeySpec[] }> = {
    c6: lipoSwapped ? LIPO_LINE_40C : LIPO_LINE,
    c7: GROUND_STATION_LINE,
    c8: ESC_LINE,
  };
  const suppliers = SUPPLIERS_BY_KEY[selected.key] ?? RICH_SUPPLIERS;
  const line = LINE_BY_KEY[selected.key] ?? { quantity: 4, description: 'Direct-drive wheel motor, 200W continuous', keySpecs: [] };

  const toggle = (key: string) =>
    setTicked((prev) => (prev.includes(key) ? prev.filter((n) => n !== key) : [...prev, key]));

  // The real switch is a BOM edit; here it flips between the two LiPo fixtures after a beat.
  const switchLipoSpec = (specGroup: string) => {
    setSwitching(specGroup);
    setTimeout(() => {
      setLipoSwapped((v) => !v);
      setSwitching(null);
    }, 900);
  };

  return (
    <Section title="Wizard step 3c — Supplier Discovery v2.1">
      <div className="flex flex-col gap-s5">
        <div className="flex flex-wrap items-center gap-s3">
          <TrustChip score={91} />
          <TrustChip score={78} />
          <TrustChip score={65} />
          <TrustChip />
          <span className="text-meta text-ink-muted">trust chip tones + honest absent-state</span>
        </div>

        <div className="grid h-[720px] grid-cols-[minmax(150px,220px)_minmax(0,1fr)] overflow-hidden rounded-card border border-line bg-surface">
          <ComponentListPane
            items={LIST_ITEMS.filter(
              (it) => !filter || it.name.toLowerCase().includes(filter.toLowerCase()) || it.partNumber?.toLowerCase().includes(filter.toLowerCase()),
            )}
            totalCount={LIST_ITEMS.length}
            selectedKey={selectedKey}
            filter={filter}
            onFilterChange={setFilter}
            onSelect={setSelectedKey}
            onRetry={() => undefined}
            retrying={{}}
          />
          <ComponentDetailPane
            key={selected.key}
            name={selected.name}
            partNumber={selected.partNumber}
            mpn={selected.mpn}
            quantity={line.quantity}
            description={line.description}
            keySpecs={line.keySpecs}
            phase={selected.phase}
            researchStage={3}
            knownSuppliers={4}
            researchState="RESEARCHING"
            suppliers={selected.phase === 'ready' ? suppliers : []}
            tickedKeys={ticked}
            onToggleListing={toggle}
            onUseAlternative={selected.key === 'c6' ? switchLipoSpec : undefined}
            switchingSpec={switching}
            onRetry={() => undefined}
            isRetrying={false}
            targetPrice={selected.key === 'c1' ? 220 : undefined}
            leadWindowWeeks={selected.key === 'c1' ? 6 : undefined}
            requiredCompliance={['ISO 9001', 'RoHS']}
            onAddToFirstMessage={(supplierName, note) => console.log('queued for first message', supplierName, note)}
          />
        </div>
        <p className="m-0 text-meta text-ink-muted">
          "Optical encoder" shows old research data — no prices, no ranks: dashes, and no matrix. "LiPo battery" is
          RFQ-134: chips for 30C (yours, 1 supplier), 40C and 35C; Robbie's one suggestion; a chip previews that spec
          read-only; "Use 40C for this line" switches it (and back). "Handheld ground station" is RFQ-135: Meridian as one
          card with two products. "ESC 30A" has no other spec, so no chips.
        </p>

        <DiscoverySpecStates />

        <div>
          <p className="m-0 mb-s3 text-table-header uppercase text-ink-muted">Detail blocks alone — rich vs. absent data (track record always dashes)</p>
          <div className="grid grid-cols-1 gap-s5 xl:grid-cols-2">
            <SupplierDetailBlocks supplier={RICH_SUPPLIERS[0]} quantity={4} requiredCompliance={['ISO 9001', 'RoHS']} onAddToFirstMessage={() => undefined} />
            <SupplierDetailBlocks supplier={LEGACY_SUPPLIERS[0]} quantity={4} requiredCompliance={['ISO 9001', 'RoHS']} />
          </div>
        </div>

        <div>
          <p className="m-0 mb-s3 text-table-header uppercase text-ink-muted">Supplier Trust Matrix — with target price split vs. trust bar only</p>
          <div className="grid grid-cols-1 gap-s5 xl:grid-cols-2">
            <SupplierTrustMatrix suppliers={RICH_SUPPLIERS} shortlistedNames={ticked} targetPrice={220} />
            <SupplierTrustMatrix suppliers={RICH_SUPPLIERS} shortlistedNames={ticked} />
          </div>
        </div>
      </div>
    </Section>
  );
};

export default DiscoveryGallery;
